import React, { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { HelpCircle, Send, Loader2, Lightbulb } from 'lucide-react';

const SUGGESTIONS = [
  "¿Cómo creo una factura?",
  "¿Cómo registro un gasto con foto?",
  "¿Cómo genero un contrato con IA?",
  "¿Cómo importo facturas antiguas?",
  "¿Qué puede hacer el Auxiliar IA?",
  "¿Cómo personalizo mi logo de empresa?",
  "¿Cómo veo el historial por trimestres?",
  "¿Cómo exporto una factura a PDF?"
];

const HelpPage = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      const scrollArea = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollArea) scrollArea.scrollTop = scrollArea.scrollHeight;
    }
  }, [messages]);

  const askQuestion = async (question) => {
    if (!question.trim()) return;
    const q = question.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: q }]);
    setLoading(true);

    try {
      const { data } = await api.post('/help/ask', { question: q });
      setMessages(prev => [...prev, { role: 'assistant', text: data.answer }]);
    } catch (err) {
      toast.error('Error obteniendo respuesta');
      setMessages(prev => [...prev, { role: 'assistant', text: 'Lo siento, hubo un error. Inténtalo de nuevo.' }]);
    } finally {
      setLoading(false);
    }
  };

  const renderMarkdown = (text) => {
    return text
      .replace(/^### (.+)$/gm, '<h3 class="font-bold text-sm mt-3 mb-1">$1</h3>')
      .replace(/^## (.+)$/gm, '<h2 class="font-bold text-base mt-4 mb-2">$1</h2>')
      .replace(/^# (.+)$/gm, '<h1 class="font-bold text-lg mt-3 mb-2">$1</h1>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/^\d+\.\s(.+)$/gm, '<li class="ml-4 list-decimal text-sm leading-relaxed">$1</li>')
      .replace(/^-\s(.+)$/gm, '<li class="ml-4 list-disc text-sm leading-relaxed">$1</li>')
      .replace(/`([^`]+)`/g, '<code class="bg-stone-200 px-1 py-0.5 rounded text-xs">$1</code>')
      .replace(/\n\n/g, '</p><p class="mb-2 text-sm leading-relaxed">')
      .replace(/\n/g, '<br/>');
  };

  return (
    <div className="h-[calc(100vh-180px)] lg:h-[calc(100vh-120px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 sm:mb-6">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#0EA5E9] flex items-center justify-center flex-shrink-0">
          <HelpCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight" data-testid="help-title">Ayuda</h1>
          <p className="text-stone-500 text-xs sm:text-sm">Pregunta cualquier cosa sobre Facturcal</p>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 flex flex-col overflow-hidden">
        <ScrollArea className="flex-1 p-3 sm:p-6" ref={scrollRef}>
          <div className="space-y-4 sm:space-y-6">
            {messages.length === 0 && (
              <div className="text-center py-8 sm:py-12">
                <HelpCircle className="w-12 h-12 text-[#0EA5E9] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-stone-700 mb-2">¿En qué puedo ayudarte?</h3>
                <p className="text-sm text-stone-500 mb-6">Pregúntame cómo usar cualquier función de Facturcal</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg mx-auto" data-testid="help-suggestions">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={`suggestion-${s.slice(0, 20)}`}
                      onClick={() => askQuestion(s)}
                      className="text-left text-sm px-3 py-2.5 bg-stone-50 hover:bg-sky-50 hover:border-sky-200 border border-stone-100 rounded-xl transition-colors flex items-start gap-2"
                      data-testid={`suggestion-${i}`}
                    >
                      <Lightbulb className="w-3.5 h-3.5 text-[#0EA5E9] mt-0.5 flex-shrink-0" />
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={`msg-${msg.role}-${i}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[90%] sm:max-w-[80%] ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-r from-[#FF6B4A] to-[#FBBF24] text-white rounded-2xl rounded-br-none'
                    : 'bg-stone-100 text-stone-900 rounded-2xl rounded-bl-none'
                } px-3 sm:px-4 py-2.5 sm:py-3`}>
                  {msg.role === 'user' ? (
                    <p className="text-sm">{msg.text}</p>
                  ) : (
                    <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`<p class="mb-2 text-sm leading-relaxed">${renderMarkdown(msg.text)}</p>`) }} />
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-stone-100 rounded-xl rounded-bl-none px-4 py-3 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-[#0EA5E9]" />
                  <span className="text-sm text-stone-500">Buscando respuesta...</span>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input */}
        <div className="p-3 sm:p-4 border-t border-stone-100">
          <form onSubmit={(e) => { e.preventDefault(); askQuestion(input); }} className="flex gap-2 sm:gap-3">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Escribe tu pregunta..."
              className="rounded-xl flex-1"
              disabled={loading}
              data-testid="help-input"
            />
            <Button type="submit" disabled={loading || !input.trim()} className="bg-[#0EA5E9] hover:bg-[#0284C7] rounded-xl" data-testid="help-send-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;
